import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, RefreshCw } from 'lucide-react';
import {
  enquiriesApi,
  ENQUIRIES_CHANGED_EVENT,
  type AdviserEnquiry,
  type EnquiryStatus,
} from '../../advisers/enquiriesApi';
import { safeWebUrl } from '../../advisers/AdviserProfileView';

const statusLabels: Record<EnquiryStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  closed: 'Closed',
};
const formatDate = (date: string) =>
  new Date(date).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export default function AdviserEnquiriesPanel({ practiceId }: { practiceId: string }) {
  const [params, setParams] = useSearchParams();
  const [enquiries, setEnquiries] = useState<AdviserEnquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const scope = useRef(practiceId);
  scope.current = practiceId;
  const readingIds = useRef(new Set<string>());
  const selectedId = params.get('enquiry');
  const selected = enquiries.find((enquiry) => enquiry.id === selectedId);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await enquiriesApi.list(practiceId);
      if (scope.current === practiceId) setEnquiries(data);
    } catch (err) {
      if (scope.current === practiceId)
        setError(err instanceof Error ? err.message : 'Unable to load enquiries.');
    } finally {
      if (scope.current === practiceId) setLoading(false);
    }
  }, [practiceId]);
  useEffect(() => {
    setEnquiries([]);
    readingIds.current.clear();
    setNotice('');
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!selected || selected.readAt || readingIds.current.has(selected.id)) return;
    readingIds.current.add(selected.id);
    const currentPractice = practiceId;
    enquiriesApi
      .update(practiceId, selected.id, { read: true })
      .then((updated) => {
        if (scope.current !== currentPractice) return;
        setEnquiries((current) =>
          current.map((enquiry) => (enquiry.id === updated.id ? updated : enquiry))
        );
        window.dispatchEvent(new Event(ENQUIRIES_CHANGED_EVENT));
      })
      .catch((err) => {
        if (scope.current !== currentPractice) return;
        setError(err instanceof Error ? err.message : 'Unable to mark this enquiry as read.');
        readingIds.current.delete(selected.id);
      });
  }, [selected?.id, selected?.readAt, practiceId]);

  const selectEnquiry = (id: string) => {
    const next = new URLSearchParams(params);
    next.set('enquiry', id);
    setParams(next);
    setNotice('');
  };
  const changeStatus = async (status: EnquiryStatus) => {
    if (!selected || saving) return;
    const currentPractice = practiceId;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const updated = await enquiriesApi.update(practiceId, selected.id, { status });
      if (scope.current !== currentPractice) return;
      setEnquiries((current) =>
        current.map((enquiry) => (enquiry.id === updated.id ? updated : enquiry))
      );
      setNotice(`Enquiry marked ${statusLabels[status].toLowerCase()}.`);
    } catch (err) {
      if (scope.current === currentPractice)
        setError(err instanceof Error ? err.message : 'Unable to update this enquiry.');
    } finally {
      if (scope.current === currentPractice) setSaving(false);
    }
  };
  return (
    <section>
      <div className="adviser-section-heading">
        <div>
          <p className="adviser-eyebrow">Conversations through StratPlan</p>
          <h2>Enquiries</h2>
          <p>Review what each business needs, then contact them to agree the next step.</p>
        </div>
        <button
          type="button"
          className="adviser-button adviser-button-secondary"
          onClick={() => void refresh()}
          disabled={loading || saving}
        >
          <RefreshCw size={16} /> Refresh
        </button>
      </div>
      {error && (
        <p className="adviser-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="adviser-notice" role="status">
          {notice}
        </p>
      )}
      {loading ? (
        <p role="status" className="adviser-empty">
          Loading enquiries…
        </p>
      ) : enquiries.length === 0 ? (
        <div className="adviser-panel adviser-empty">
          <h3>Your next conversation starts here</h3>
          <p>
            Requests from your published adviser profile will appear here. Practice owners receive a
            notification and an email for each new enquiry.
          </p>
        </div>
      ) : (
        <div className="adviser-enquiries-grid">
          <ul className="adviser-enquiries-list" aria-label="Conversation enquiries">
            {enquiries.map((enquiry) => (
              <li key={enquiry.id}>
                <button
                  type="button"
                  className="adviser-enquiry-item"
                  aria-current={selected?.id === enquiry.id ? 'true' : undefined}
                  onClick={() => selectEnquiry(enquiry.id)}
                >
                  <div className="adviser-enquiry-item-head">
                    <strong>{enquiry.company}</strong>
                    {!enquiry.readAt && <span className="adviser-enquiry-unread">Unread</span>}
                  </div>
                  <span>
                    {enquiry.name} · {enquiry.helpWith}
                  </span>
                  <span>
                    {formatDate(enquiry.createdAt)} · {statusLabels[enquiry.status]}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {selected ? (
            <article
              className="adviser-panel adviser-enquiry-details"
              aria-label={`Enquiry from ${selected.name}`}
            >
              <p className="adviser-eyebrow">{selected.company}</p>
              <h3>{selected.name}</h3>
              <p className="adviser-muted">Received {formatDate(selected.createdAt)}</p>
              <dl>
                <div>
                  <dt>Looking for help with</dt>
                  <dd>{selected.helpWith}</dd>
                </div>
                <div>
                  <dt>Goal or challenge</dt>
                  <dd className="adviser-preserve-lines">{selected.message}</dd>
                </div>
                <div>
                  <dt>Timeframe</dt>
                  <dd>{selected.timeframe}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>
                    <a className="adviser-text-link" href={`mailto:${selected.email}`}>
                      {selected.email}
                    </a>
                  </dd>
                </div>
                {selected.phone && (
                  <div>
                    <dt>Phone</dt>
                    <dd>
                      <a
                        className="adviser-text-link"
                        href={`tel:${selected.phone.replace(/[^+\d]/g, '')}`}
                      >
                        {selected.phone}
                      </a>
                    </dd>
                  </div>
                )}
                {safeWebUrl(selected.website) && (
                  <div>
                    <dt>Website</dt>
                    <dd>
                      <a
                        className="adviser-text-link"
                        href={safeWebUrl(selected.website)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {selected.website}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
              <a
                className="adviser-button"
                href={`mailto:${selected.email}?subject=${encodeURIComponent(`Your conversation with ${selected.firmName}`)}`}
              >
                <Mail size={16} /> Reply by email
              </a>
              <p className="adviser-muted" style={{ marginTop: 12 }}>
                Opens your email app. Update the status after you have responded.
              </p>
              <label className="adviser-enquiry-status-control">
                Enquiry status
                <select
                  value={selected.status}
                  disabled={saving}
                  onChange={(event) => void changeStatus(event.target.value as EnquiryStatus)}
                >
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </article>
          ) : (
            <div className="adviser-panel adviser-empty">
              <h3>{selectedId ? 'Enquiry unavailable' : 'Choose an enquiry'}</h3>
              <p>
                {selectedId
                  ? 'This enquiry is not available in the selected practice. Choose a request from the list.'
                  : 'View the business, their goal and contact details.'}
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
